
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InventoryMovement, InventoryMovementItem, Warehouse, Book, User, InventoryMovementHistory, AppSettings, Items } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Search,
  ArrowLeftRight,
  Truck,
  MinusCircle,
  Eye,
  Edit,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import InventoryMovementForm from "./InventoryMovementForm";
import ViewInventoryMovementModal from "./ViewInventoryMovementModal";
import EditInventoryMovementModal from "./EditInventoryMovementModal";

export default function InventoryMovementsView() {
  const { t } = useTranslation();
  
  const movementTypes = {
    receipt: { label: t('inventory.movements.types.receipt'), icon: Plus, color: 'bg-green-100 text-green-800 border-green-200' },
    transfer: { label: t('inventory.movements.types.transfer'), icon: ArrowLeftRight, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    shipment: { label: t('inventory.movements.types.shipment'), icon: Truck, color: 'bg-orange-100 text-orange-800 border-orange-200' },
    write_off: { label: t('inventory.movements.types.write_off'), icon: MinusCircle, color: 'bg-red-100 text-red-800 border-red-200' }
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800 border-gray-200',
    executed: 'bg-green-100 text-green-800 border-green-200',
    payment_pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    payment_complete: 'bg-teal-100 text-teal-800 border-teal-200',
    payment_expired: 'bg-red-100 text-red-800 border-red-200'
  };

  const statusLabels = {
      draft: t('inventory.movements.status.draft'),
      executed: t('inventory.movements.status.executed'),
      payment_pending: t('inventory.movements.status.payment_pending'),
      payment_complete: t('inventory.movements.status.payment_complete'),
      payment_expired: t('inventory.movements.status.payment_expired')
  };

  const getFieldName = (key) => ({
      document_number: t('inventory.movements.fields.document_number'),
      document_date: t('inventory.movements.fields.document_date'),
      created_date: t('inventory.movements.fields.created_date'),
      from_warehouse_id: t('inventory.movements.fields.from_warehouse_id'),
      to_warehouse_id: t('inventory.movements.fields.to_warehouse_id'),
      basis: t('inventory.movements.fields.basis'),
      notes: t('inventory.movements.fields.notes')
  }[key] || key);

  const [movements, setMovements] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [books, setBooks] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingMovement, setViewingMovement] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [editingMovementItems, setEditingMovementItems] = useState([]);

  useEffect(() => {
    loadData();
    User.me().then(setUser).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    filterMovements();
  }, [movements, searchTerm, typeFilter]);

  const normalizeMovements = (raw) => {
    // Группируем transfer_out + transfer_in (parent_id) в одну строку
    const inByParent = new Map();
    for (const m of raw) {
      if ((m.type === 'transfer_in' || m.document_type === 'transfer') && m.parent_id) {
        inByParent.set(m.parent_id, m);
      }
    }

    const result = [];
    for (const m of raw) {
      if (m.type === 'transfer_in' && m.parent_id) {
        // пропускаем child — он будет показан в паре
        continue;
      }
      if (m.type === 'transfer_out' || (m.document_type === 'transfer' && !m.parent_id)) {
        const inPair = inByParent.get(m.id);
        result.push({
          ...m,
          document_type: 'transfer',
          from_warehouse_id: m.warehouse_id,
          to_warehouse_id: inPair?.warehouse_id,
          paired_movement_id: inPair?.id
        });
        continue;
      }
      result.push(m);
    }
    return result;
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [movementsData, warehousesData, booksData, itemsData] = await Promise.all([
        InventoryMovement.list('-created_date'),
        Warehouse.list({ is_active: true }),
        Book.list(),
        Items.list({ is_active: true })
      ]);

      setMovements(normalizeMovements(movementsData));
      setWarehouses(warehousesData);
      setBooks(booksData);
      setItems(itemsData);

    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  };

  const filterMovements = () => {
    let filtered = movements;

    if (searchTerm) {
      filtered = filtered.filter(movement =>
        movement.document_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.basis?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== "all") {
      filtered = filtered.filter(movement => movement.document_type === typeFilter);
    }

    setFilteredMovements(filtered);
  };

  const handleMovementCreate = async (movementData) => {
    // Transform form data to match backend API expectations
    const movement = movementData.movement;
    const items = movementData.items;
    
    // Map frontend operation types to backend movement types
    const typeMapping = {
      'receipt': 'in',
      'shipment': 'out', 
      'write_off': 'out',
      'transfer': 'transfer'
    };
    
    // Map frontend operation types to document types
    const documentTypeMapping = {
      'receipt': 'receipt',
      'shipment': 'shipment', 
      'write_off': 'write_off',
      'transfer': 'transfer'
    };
    
    // Determine warehouse_id based on operation type
    let warehouse_id;
    if (movement.type === 'receipt') {
      warehouse_id = movement.to_warehouse_id;
    } else if (movement.type === 'shipment' || movement.type === 'write_off') {
      warehouse_id = movement.from_warehouse_id;
    } else if (movement.type === 'transfer') {
      // For transfers, we'll use the destination warehouse
      warehouse_id = movement.to_warehouse_id;
    }
    
    // Ensure we have a valid warehouse_id
    if (!warehouse_id) {
      throw new Error('Warehouse ID is required');
    }
    
    // Transform items to match backend expectations
    const transformedItems = items.map(item => ({
      item_id: item.item_id,
      quantity: item.quantity
    }));
    
    // Create the movement with the correct data structure
    const movementPayload = {
      document_number: movement.document_number,
      document_date: movement.document_date,
      document_type: documentTypeMapping[movement.type],
      type: typeMapping[movement.type],
      warehouse_id: warehouse_id,
      // передаём оба склада для transfer
      from_warehouse_id: movement.from_warehouse_id,
      to_warehouse_id: movement.to_warehouse_id,
      basis: movement.basis,
      items: transformedItems,
      notes: movement.notes || movement.basis || ''
    };

    const newMovement = await InventoryMovement.create(movementPayload);

    if (user) {
      if (movement.type === 'transfer' && newMovement && newMovement.transfer_out && newMovement.transfer_in) {
        await InventoryMovementHistory.create({
          movement_id: newMovement.transfer_out.id,
          user_name: user.full_name,
          action: 'created',
          details: `Накладная (transfer OUT) #${newMovement.transfer_out.document_number || newMovement.transfer_out.id} создана`
        });
        await InventoryMovementHistory.create({
          movement_id: newMovement.transfer_in.id,
          user_name: user.full_name,
          action: 'created',
          details: `Накладная (transfer IN) #${newMovement.transfer_in.document_number || newMovement.transfer_in.id} создана`
        });
      } else if (newMovement?.id) {
        await InventoryMovementHistory.create({
          movement_id: newMovement.id,
          user_name: user.full_name,
          action: 'created',
          details: `Накладная #${newMovement.document_number || newMovement.id} создана`
        });
      }
    }

    await loadData();
    setShowCreateModal(false);
  };

  const handleExecuteMovement = async (movementId) => {
    await InventoryMovement.update(movementId, { status: 'executed' });
    if (user) {
        await InventoryMovementHistory.create({
            movement_id: movementId,
            user_name: user.full_name,
            action: 'status_changed',
            details: 'Статус изменен на "Проведен"'
        });
    }
    await loadData();
  };

  const handleReactivateMovement = async (movementId) => {
    await InventoryMovement.update(movementId, { status: 'payment_pending' });
    if (user) {
        await InventoryMovementHistory.create({
            movement_id: movementId,
            user_name: user.full_name,
            action: 'status_changed',
            details: 'Статус изменен на "Ожидает оплаты" (возобновление после просрочки)'
        });
    }
    await loadData();
  };

  const handleViewClick = (movement) => {
      setViewingMovement(movement);
      setIsViewModalOpen(true);
  };

  const handleEditClick = async (movement) => {
      // Get items from the movement's JSONB field instead of separate table
      const items = movement.items || [];
      setEditingMovementItems(items);
      setEditingMovement(movement);
      setIsEditModalOpen(true);
  };

  const handleDeleteClick = async (movement) => {
      if (!confirm(t('inventory.movements.actions.confirm_delete'))) {
          return;
      }

      try {
          await InventoryMovement.delete(movement.id);
          
          if (user) {
              await InventoryMovementHistory.create({
                  movement_id: movement.id,
                  user_name: user.full_name,
                  action: 'deleted',
                  details: `Накладная ${movement.document_number || movement.id} удалена`
              });
          }

          await loadData();
      } catch (error) {
          console.error('Error deleting movement:', error);
          alert(t('inventory.movements.actions.delete_error'));
      }
  };

  const handleMovementSave = async (updatedData, updatedItems) => {
      const original = editingMovement;
      let changes = [];

      // Check for header changes
      for(const key in updatedData) {
          if (updatedData[key] !== original[key] && getFieldName(key)) {
              changes.push(`Поле "${getFieldName(key)}" изменено.`);
          }
      }

      // Update the main document with items in JSONB field
      const dataToUpdate = {
          ...updatedData,
          items: updatedItems // Store items directly in JSONB field
      };

      await InventoryMovement.update(original.id, dataToUpdate);

      if (changes.length === 0) {
          changes.push("Обновлены позиции накладной.");
      }

      if (user) {
          await InventoryMovementHistory.create({
              movement_id: original.id,
              user_name: user.full_name,
              action: 'updated',
              details: `Внесены изменения: ${changes.join(' ')}`
          });
      }

      setIsEditModalOpen(false);
      setEditingMovement(null);
      setEditingMovementItems([]);
      await loadData();
  };


  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : '-';
  };

  const getEntityName = (movement, isFrom = true) => {
    if (isFrom) {
      // Для поля "From"
      if (movement.from_warehouse_id) {
        return getWarehouseName(movement.from_warehouse_id);
      } else if (movement.from_entity_name) {
        return movement.from_entity_name;
      } else if (movement.from_entity_type === 'printing_house') {
        return 'Типография';
      } else if (movement.from_entity_type === 'customer') {
        return 'Клиент';
      }
      // Для приходных накладных (receipt) поле From должно быть пустым
      // Для движений типа "shipment" (отгрузка) показываем основной склад
      if (movement.document_type === 'shipment' && movement.warehouse_id) {
        return getWarehouseName(movement.warehouse_id);
      }
      // Для движений типа "write_off" (списание) показываем основной склад
      if (movement.document_type === 'write_off' && movement.warehouse_id) {
        return getWarehouseName(movement.warehouse_id);
      }
      // Для агрегированного transfer используем склад исходящей записи
      if (movement.document_type === 'transfer' && movement.from_warehouse_id) {
        return getWarehouseName(movement.from_warehouse_id);
      }
      return '-';
    } else {
      // Для поля "To"
      // Для shipment показываем получателя из заказа, если есть
      if (movement.document_type === 'shipment') {
        return movement.customer_name || '-';
      }
      if (movement.to_warehouse_id) {
        return getWarehouseName(movement.to_warehouse_id);
      } else if (movement.to_entity_name) {
        return movement.to_entity_name;
      } else if (movement.to_entity_type === 'printing_house') {
        return 'Типография';
      } else if (movement.to_entity_type === 'customer') {
        return 'Клиент';
      } else if (movement.to_entity_type === 'disposal') {
        return 'Списание';
      }
      
      // Для движений типа "receipt" (приход) показываем основной склад
      if (movement.document_type === 'receipt' && movement.warehouse_id) {
        return getWarehouseName(movement.warehouse_id);
      }
      
      // Для движений типа "shipment" (отгрузка) показываем основной склад в поле "From"
      if (movement.document_type === 'shipment' && movement.warehouse_id) {
        return getWarehouseName(movement.warehouse_id);
      }
      
      // Для движений типа "write_off" (списание) показываем основной склад в поле "From"
      if (movement.document_type === 'write_off' && movement.warehouse_id) {
        return getWarehouseName(movement.warehouse_id);
      }
      // Для агрегированного transfer используем склад входящей записи
      if (movement.document_type === 'transfer' && movement.to_warehouse_id) {
        return getWarehouseName(movement.to_warehouse_id);
      }
      
      return '-';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-slate-800">{t('inventory.movements.title')}</h2>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('inventory.movements.new_invoice')}
        </Button>
      </div>

      <Card className="elegant-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={t('inventory.movements.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
            <Tabs value={typeFilter} onValueChange={setTypeFilter}>
                <TabsList>
                    <TabsTrigger value="all">{t('inventory.movements.filters.all')}</TabsTrigger>
                    {Object.entries(movementTypes).map(([key, {label}]) => (
                        <TabsTrigger key={key} value={key}>{label}</TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      <Card className="elegant-card overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.number')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.type')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.from')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.to')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.basis')}</TableHead>
                <TableHead className="font-bold text-slate-900">{t('inventory.movements.table.status')}</TableHead>
                <TableHead className="font-bold text-slate-900 text-right">{t('inventory.movements.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMovements.map((movement) => {
                const typeInfo = movementTypes[movement.document_type] || movementTypes.receipt;
                return (
                  <TableRow key={movement.id} className="hover:bg-slate-50/50">
                    <TableCell>
                      <p className="font-medium text-slate-900">{movement.document_number || movement.id}</p>
                      <p className="text-sm text-slate-500">
                        {movement.document_date 
                          ? format(new Date(movement.document_date), 'dd.MM.yyyy')
                          : format(new Date(movement.created_date), 'dd.MM.yyyy')
                        }
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                    </TableCell>
                    <TableCell>{getEntityName(movement, true)}</TableCell>
                    <TableCell>{getEntityName(movement, false)}</TableCell>
                    <TableCell className="text-slate-600">
                      {movement.document_type === 'shipment' && movement.order_id ? (
                        <a
                          href={`/panel/orders?order_id=${movement.order_id}`}
                          className="text-amber-700 hover:text-amber-800 underline decoration-amber-400"
                          title={t('inventory.movements.open_order')}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {movement.basis}
                        </a>
                      ) : (
                        movement.basis
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[movement.status]}>
                        {statusLabels[movement.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                       <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleViewClick(movement)}>
                              <Eye className="w-4 h-4" />
                          </Button>
                          {(movement.status === 'draft' || movement.status === 'payment_expired' || movement.status === 'payment_pending' || movement.status === 'payment_complete') && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => handleEditClick(movement)}>
                                    <Edit className="w-4 h-4" />
                                </Button>
                                {movement.status === 'draft' && (
                                    <>
                                        <Button variant="outline" size="sm" onClick={() => handleExecuteMovement(movement.id)}>
                                          {t('inventory.movements.actions.execute')}
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => handleDeleteClick(movement)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            title={t('inventory.movements.actions.delete')}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </>
                                )}
                                {movement.status === 'payment_expired' && (
                                    <Button variant="outline" size="sm" onClick={() => handleReactivateMovement(movement.id)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                                      {t('inventory.movements.actions.reactivate')}
                                    </Button>
                                )}
                              </>
                          )}
                       </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InventoryMovementForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleMovementCreate}
        warehouses={warehouses}
        items={items}
      />

      <ViewInventoryMovementModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        onEdit={handleEditClick}
        movement={viewingMovement}
        books={books}
        items={items}
        warehouses={warehouses}
      />

      <EditInventoryMovementModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleMovementSave}
        movement={editingMovement}
        initialItems={editingMovementItems}
        warehouses={warehouses}
        books={books}
        items={items}
      />
    </div>
  );
}
